"use client"
import React, { useState, useEffect } from 'react';
import './Contact.css';
import { motion } from 'framer-motion';
import ReCAPTCHA from "react-google-recaptcha";

import { Sparkels } from '../../Components';
import { Spotlight } from '../../ui/spotlight';
import { main_server } from '../../constants';

export default function Contact(props) {
  const [email, setEmail] = useState('');
  const [subject, setSubject] = useState('');
  const [text, setText] = useState('');
  const [showAlert, setShowAlert] = useState(false);
  const [val, setVal] = useState(''); // captcha response  
  const [isHovered, setIsHovered] = useState(false);

  // Update input group classes based on content
  useEffect(() => {
    const updateInputClasses = () => {
      const emailGroup = document.querySelector('[data-input="email-contact"]')?.parentElement;
      const subjectGroup = document.querySelector('[data-input="subject-contact"]')?.parentElement;
      const messageGroup = document.querySelector('[data-input="message-contact"]')?.parentElement;

      if (emailGroup) {
        emailGroup.classList.toggle('filled', email.length > 0);
      }
      if (subjectGroup) {
        subjectGroup.classList.toggle('filled', subject.length > 0);
      }
      if (messageGroup) {
        messageGroup.classList.toggle('filled', text.length > 0);
      }
    };

    updateInputClasses();
  }, [email, subject, text]);

  return (
    <div className='contact_container'>
      <Spotlight 
        className="top-40 left-0 md:left-60 md:-top-20" 
        fill="rgba(255, 255, 255, 0.1)" 
        active={true}
      />
      
      {/* Floating particles background */}
      <div className="floating-particles">
        {[...Array(20)].map((_, i) => (
          <div 
            key={i} 
            className="particle" 
            style={{
              left: `${Math.random() * 100}%`,
              animationDelay: `${Math.random() * 1}s`,
              animationDuration: `${15 + Math.random() * 10}s`
            }}
          />
        ))}
      </div>

      <motion.div
        initial={{ opacity: 0.0, y: 40 }}
        whileInView={{ opacity: 1, y: 0 }}
        transition={{
          delay: 0.3,
          duration: 0.8,
          ease: "easeInOut",
        }}
        className="contact_header"
      >
        <motion.h3 
          className='contact_title'
          initial={{ scale: 0.9 }}
          animate={{ scale: 1 }}
          transition={{ duration: 0.5, delay: 0.5 }}
        >
          Get In Touch
        </motion.h3>
        <Sparkels />
        <motion.div 
          className="contact_subtitle"
          initial={{ opacity: 0 }}
          animate={{ opacity: 1 }}
          transition={{ delay: 0.8 }}
        >
          We'd love to hear from you. Send us a message and we'll respond as soon as possible.
        </motion.div>
      </motion.div>

      <motion.div 
        className='contact_form_container'
        initial={{ opacity: 0, y: 50 }}
        whileInView={{ opacity: 1, y: 0 }}
        transition={{ delay: 0.5, duration: 0.8 }}
        onHoverStart={() => setIsHovered(true)}
        onHoverEnd={() => setIsHovered(false)}
      >
        <motion.div 
          className="contact_form_glow"
          animate={{ 
            opacity: isHovered ? 0.8 : 0.4,
            scale: isHovered ? 1.02 : 1 
          }}
          transition={{ duration: 0.3 }}
        />
        
        <form autoComplete="off" id='contactForm' className='contact_form'>
          <div className="form_inputs_grid">
            <motion.div 
              className="input_group"
              whileHover={{ scale: 1.02 }}
              transition={{ duration: 0.2 }}
            >
              <label className="input_label" data-label='email-contact'>Email</label>
              <input 
                autoComplete="off" 
                className="input_field" 
                value={email} 
                onChange={(e) => setEmail(e.target.value)}
                type="email" 
                name='email-contact' 
                id='email-contact' 
                data-input='email-contact' 
                required 
              />
              <div className="input_border" />
            </motion.div>

            <motion.div 
              className="input_group"
              whileHover={{ scale: 1.02 }}
              transition={{ duration: 0.2 }}
            >
              <label className="input_label" data-label='subject-contact'>Subject</label>
              <input 
                autoComplete="off" 
                className="input_field" 
                value={subject} 
                onChange={(e) => setSubject(e.target.value)}
                type="text" 
                name='subject-contact' 
                id='subject-contact' 
                data-input='subject-contact' 
                required 
              />
              <div className="input_border" />
            </motion.div>

            <motion.div 
              className="input_group textarea_group"
              whileHover={{ scale: 1.01 }}
              transition={{ duration: 0.2 }}
            >
              <label className="input_label" data-label='message-contact'>Message</label>
              <textarea 
                autoComplete="off" 
                className="input_field textarea_field" 
                value={text} 
                onChange={(e) => setText(e.target.value)}
                data-input='message-contact' 
                required 
              />
              <div className="input_border" />
            </motion.div>
          </div>
          
          <div className="captcha_container">
            <ReCAPTCHA 
              onChange={(e) => {
                setVal(e as string);
              }} 
              sitekey="6Lc3670pAAAAANhR_fYQnuS8a9XtOBvxcVqZ1z3F" 
            />
          </div>

          <motion.div 
            className="submit_container"
            whileHover={{ scale: 1.05 }}
            whileTap={{ scale: 0.95 }}
          >
            <button
              data-action='submit' 
              onClick={(e) => {
                e.preventDefault();
                if (subject.length > 32 || subject.length < 3) {
                  props.setAlertText('Subject should be between 3 and 32 characters');
                  props.setAlertType(false);
                  props.setShowAlert(true);
                  setTimeout(() => props.setShowAlert(false), 2500);
                  return;
                }
                if (text.length > 256 || text.length < 3) {
                  props.setAlertText('Description should be between 3 and 256 characters');
                  props.setAlertType(false);
                  props.setShowAlert(true);
                  setTimeout(() => props.setShowAlert(false), 2500);
                  return;
                }
                if (!email.trim().match(/^(([^<>()[\]\.,;:\s@\"]+(\.[^<>()[\]\.,;:\s@\"]+)*)|(\".+\"))@(([^<>()[\]\.,;:\s@\"]+\.)+[^<>()[\]\.,;:\s@\"]{2,})$/i)) {
                  props.setAlertText('Email is invalid, Please enter a correct format');
                  props.setAlertType(false);
                  props.setShowAlert(true);
                  setTimeout(() => props.setShowAlert(false), 2500);
                  return;
                }
                if (val.length == 0) {
                  props.setAlertText('Please Complete The Validation');
                  props.setAlertType(false);
                  props.setShowAlert(true);
                  setTimeout(() => props.setShowAlert(false), 2500);
                  return;
                }
                const xhr = new XMLHttpRequest();
                xhr.onreadystatechange = () => {
                  if (xhr.readyState === XMLHttpRequest.DONE) {
                    if (xhr.status === 200) {
                      props.setAlertText('Message sent successfully !');
                      props.setAlertType(true);
                      props.setShowAlert(true);
                      setEmail("");
                      setSubject("");
                      setText("");
                      setTimeout(() => props.setShowAlert(false), 2500);
                    } else if (xhr.status === 406) {
                      props.setAlertText('Invalid form parameters ! Please revise');
                      props.setAlertType(false);
                      props.setShowAlert(true);
                      setTimeout(() => props.setShowAlert(false), 2500);
                    } else if (xhr.status === 399) {
                      props.setAlertText('Email already exists ! Please choose a new one');
                      props.setAlertType(false);
                      props.setShowAlert(true);
                      setTimeout(() => props.setShowAlert(false), 2500);
                    } else {
                      props.setAlertText('Unexpected error ! Please try again later');
                      props.setAlertType(false);
                      props.setShowAlert(true);
                      setTimeout(() => props.setShowAlert(false), 2500);
                    }
                  }
                }
                xhr.open('POST', main_server + 'contact-web');
                xhr.setRequestHeader("Content-Type", "text/plain");
                xhr.send(JSON.stringify({email: email.trim(), subject, message: text, val}));
              }} 
              id='submit-btn'
              className="submit_button"
            >
              <span className="button_text">Send Message</span>
              <div className="button_glow" />
            </button>
          </motion.div>
        </form>
      </motion.div>
    </div>
  )
}
